import {
  BookingPackageType,
  BookingProductType,
  BookingState,
  BookingTariff,
  BookingTariffType,
  isBookingProductTypePackage,
  isBookingProductTypeTariff
} from '@products/models/booking.model';
import { AppConstants } from '@shared/app-constants';
import { cloneDeep, difference, filter, forEach, isEmpty, slice, takeRight } from 'lodash';

/**
 * Find tariff type index
 * @description Get index of specified tariff type
 * @param tariffType
 * @param bookedProductTypes
 * @returns Ticket type index or -1 if index not found
 */
export const findTariffTypeIndex = (
  tariffType: BookingTariffType,
  bookedProductTypes: BookingProductType[]
): number => {
  if (!bookedProductTypes.length) {
    return AppConstants.INDEX_NOT_FOUND;
  }

  const { productType: tariffProductType, ticketTypeId: productTicketTypeId } = tariffType;

  const findIndexByTicketTypeId = (bookingProductType: BookingTariffType) => {
    if (!isBookingProductTypeTariff(bookingProductType)) {
      return false;
    }

    const { productType, ticketTypeId } = bookingProductType;

    return productType === tariffProductType && ticketTypeId === productTicketTypeId;
  };

  return bookedProductTypes.findIndex(findIndexByTicketTypeId);
};

/**
 * Find tariff index
 * @description Get index of specified ticket
 * @param tariff
 * @param bookedTariffTypes
 * @returns Ticket index or -1 if index not found
 */
export const findTariffIndex = (tariff: BookingTariff, bookedTariffTypes: BookingTariffType): number => {
  return bookedTariffTypes.tariffs.findIndex(
    (bookedTariff) =>
      bookedTariff.ticketPersonId === tariff.ticketPersonId && bookedTariff.voucherCode === tariff.voucherCode
  );
};

/**
 * Find package index
 * @description Get index of specified package
 * @param productType
 * @param cartProductTypes
 * @returns Package index or -1 if index not found
 */
export const findPackageIndex = (productType: BookingPackageType, bookedProductTypes: BookingProductType[]): number => {
  if (!bookedProductTypes.length) {
    return AppConstants.INDEX_NOT_FOUND;
  }

  const {
    productType: packageProductType,
    packageNumber: productPackageNumber,
    packageIndex: productPackageIndex
  } = productType;

  const findIndexByPackageNumberAndPackageIndex = (bookedProductType: BookingPackageType): boolean => {
    if (!isBookingProductTypePackage(bookedProductType)) {
      return false;
    }

    const { productType, packageNumber, packageIndex } = bookedProductType;

    return (
      packageProductType === productType &&
      productPackageNumber === packageNumber &&
      productPackageIndex === packageIndex
    );
  };

  return bookedProductTypes.findIndex(findIndexByPackageNumberAndPackageIndex);
};

/**
 * Get product type cart index
 * @description Get cart index of specified product type
 * @param productType
 * @param bookingProductTypes
 * @returns Product type cart index or -1 if index not found
 */
export const findProductTypeBookingIndex = (
  productType: BookingProductType,
  bookedProductTypes: BookingProductType[]
): number => {
  if (!bookedProductTypes.length) {
    return AppConstants.INDEX_NOT_FOUND;
  }

  if (isBookingProductTypeTariff(productType)) {
    return findTariffTypeIndex(productType, bookedProductTypes);
  } else if (isBookingProductTypePackage(productType)) {
    return findPackageIndex(productType, bookedProductTypes);
  }

  return AppConstants.INDEX_NOT_FOUND;
};

/**
 * Validate product types
 * @description Validate if provided products have valid count
 * @param productType
 * @returns Product types or empty array
 */
export const validateProductTypes = (productType: BookingProductType): BookingProductType => {
  if (isBookingProductTypeTariff(productType)) {
    productType.tariffs = productType.tariffs.filter((addedTariff) => addedTariff.count > 0);
  } else if (isBookingProductTypePackage(productType)) {
    productType.productTypes = productType.productTypes.filter((addedPackageProductType) => {
      if (isBookingProductTypeTariff(addedPackageProductType)) {
        addedPackageProductType.tariffs = addedPackageProductType.tariffs.filter(
          (addedPackageTariff) => addedPackageTariff.count > 0
        );

        return addedPackageProductType.tariffs.length;
      }
    });
  }

  return productType;
};

/**
 * Find booked tariff
 * @description Find booked tariff
 * @param bookedProductType
 * @param ticketTypeId
 * @param ticketPersonId
 * @param voucherCode
 * @returns Booked Tariff
 */
export const findBookedTariff = (
  bookedProductType: BookingTariffType,
  ticketTypeId: number,
  ticketPersonId: number,
  voucherCode: string
) =>
  bookedProductType.tariffs.find(
    (bookedTariff) =>
      bookedTariff.ticketTypeId === ticketTypeId &&
      bookedTariff.ticketPersonId === ticketPersonId &&
      bookedTariff.voucherCode === voucherCode
  );

/**
 * Find booked package tariff
 * @description Find booked package tariff
 * @param bookedProductType
 * @param ticketTypeId
 * @param ticketPersonId
 * @param voucherCode
 * @param packageNumber
 * @param packageIndex
 * @returns Booked Tariff
 */
export const findBookedPackageTariff = (
  bookedPackageProductType: BookingTariffType,
  ticketTypeId: number,
  ticketPersonId: number,
  voucherCode: string,
  packageNumber: number,
  packageIndex: number
) =>
  bookedPackageProductType.tariffs.find(
    (bookedPackageTariff) =>
      bookedPackageTariff.ticketTypeId === ticketTypeId &&
      bookedPackageTariff.ticketPersonId === ticketPersonId &&
      bookedPackageTariff.voucherCode === voucherCode &&
      bookedPackageTariff.packageNumber === packageNumber &&
      bookedPackageTariff.packageIndex === packageIndex
  );

/**
 * Check valid product type
 * @description Check if product has products or product types (if package)
 * @param productType
 * @returns Boolean valid product type
 */
export const isValidProductType = (productType: BookingProductType): boolean => {
  const isTariffProductTypeValid = isBookingProductTypeTariff(productType) && !!productType.tariffs.length;
  const isPackageProductTypeValid = isBookingProductTypePackage(productType) && !!productType.productTypes.length;

  return isTariffProductTypeValid || isPackageProductTypeValid;
};

/**
 * Add or remove tariff
 * @description Add or remove tariff from booking tariff types
 * @param tariffIndex
 * @param isValidTariffType
 * @param validatedTariff
 * @param bookingTariffs
 */
export const addOrRemoveTariff = (
  index: number,
  isValidTariffType: boolean,
  setTariff: BookingTariff,
  bookingTariffs: BookingTariff[]
): void => {
  if (index === AppConstants.INDEX_NOT_FOUND) {
    if (isValidTariffType) {
      bookingTariffs.push(setTariff);
    }
  } else {
    if (isValidTariffType) {
      // Remove holder uuid assign when holders will be assigned in personalization step
      const alreadyBookedTariff = bookingTariffs[index];
      const alreadyBookedTariffUuids = alreadyBookedTariff.tariffUuids;
      const alreadyBookedTariffWorkshops = alreadyBookedTariff.workshops;
      const alreadyBookedContingentReservations = alreadyBookedTariff.contingentReservations;
      const alreadyBookedAmanoParkingReservations = alreadyBookedTariff.amanoParkingReservations;
      const alreadyBookedParkingReservations = alreadyBookedTariff.parkingReservations;
      const addBooking = setTariff.count > alreadyBookedTariff.count;
      const removeBooking = setTariff.count < alreadyBookedTariff.count;

      if (addBooking) {
        setTariff.tariffUuids = [...alreadyBookedTariffUuids, ...setTariff.tariffUuids];
        setTariff.workshops = [...bookingTariffs[index].workshops, ...setTariff.workshops];
        setTariff.contingentReservations = [
          ...alreadyBookedContingentReservations,
          ...setTariff.contingentReservations
        ];
        setTariff.amanoParkingReservations = [
          ...alreadyBookedAmanoParkingReservations,
          ...setTariff.amanoParkingReservations
        ];
        setTariff.parkingReservations = [...alreadyBookedParkingReservations, ...setTariff.parkingReservations];
      } else if (removeBooking) {
        const removeBookingsCount = alreadyBookedTariff.count - setTariff.count;

        if (!isEmpty(alreadyBookedTariffWorkshops)) {
          const removedTariffUuids = takeRight(alreadyBookedTariffUuids, removeBookingsCount);

          forEach(
            alreadyBookedTariffWorkshops,
            (bookedWorkshop) =>
              (bookedWorkshop.tariffUuids = difference(bookedWorkshop.tariffUuids, removedTariffUuids))
          );

          setTariff.workshops = filter(
            alreadyBookedTariffWorkshops,
            (bookedWorkshop) => !!bookedWorkshop.tariffUuids.length
          );
        }

        setTariff.tariffUuids = slice(alreadyBookedTariffUuids, 0, -removeBookingsCount);
        setTariff.contingentReservations = slice(alreadyBookedContingentReservations, 0, -removeBookingsCount);
        setTariff.amanoParkingReservations = slice(alreadyBookedAmanoParkingReservations, 0, -removeBookingsCount);
        setTariff.parkingReservations = slice(alreadyBookedParkingReservations, 0, -removeBookingsCount);
      } else {
        setTariff.tariffUuids = alreadyBookedTariffUuids;
      }

      const setBookingTariffPriceToAmanoParkingReservationPriceTotal = setTariff.amanoParkingReservations.length;
      if (setBookingTariffPriceToAmanoParkingReservationPriceTotal) {
        setTariff.price = setTariffPriceToAmanoParkingReservationTotalPrice(setTariff);
      }

      bookingTariffs[index] = setTariff;
    } else {
      bookingTariffs.splice(index, 1);
    }
  }
};

/**
 * Remove empty product type
 * @description Remove empty product type from booked product types
 * @param products
 * @param bookedProductTypes
 * @param index
 */
export const removeEmptyProductType = (
  products: BookingProductType[] | BookingTariff[],
  bookedProductTypes: BookingProductType[],
  index: number
): void => {
  if (isEmpty(products)) {
    bookedProductTypes.splice(index, 1);
  }
};

/**
 * Calculate booked amano parking tariff price
 * @description Calculate amano parking tariff price based on amano parking reservations total price
 * @param bookedTariff
 * @return Number
 */
export const setTariffPriceToAmanoParkingReservationTotalPrice = (bookedTariff: BookingTariff) => {
  return bookedTariff.amanoParkingReservations.reduce(
    (bookedAmanoParkingReservationTotalPrice, bookedAmanoParkingReservation) =>
      bookedAmanoParkingReservationTotalPrice + bookedAmanoParkingReservation.price,
    0
  );
};

/**
 * Set product in booking list
 * @description Set product in booking list
 * @param state
 * @param props
 * @returns State
 */
export const setProductInBookingList = (state: BookingState, props: { bookingProductTypes: BookingProductType[] }) => {
  const cloneStateProductsList: BookingProductType[] = cloneDeep(state.list);
  const setProductTypes: BookingProductType[] = cloneDeep(props.bookingProductTypes);

  if (setProductTypes.length) {
    setProductTypes.forEach((setProductType) => {
      const productTypeBookingIndex = findProductTypeBookingIndex(setProductType, cloneStateProductsList);

      if (productTypeBookingIndex === AppConstants.INDEX_NOT_FOUND) {
        const validatedProductType = validateProductTypes(setProductType);

        if (isValidProductType(validatedProductType)) {
          cloneStateProductsList.push(validatedProductType);
        }
      } else if (isBookingProductTypeTariff(setProductType)) {
        const foundBookedTariffType = cloneStateProductsList[productTypeBookingIndex] as BookingTariffType;
        const { tariffs: foundBookedTariffs } = foundBookedTariffType;

        setProductType.tariffs.forEach((setTariff) => {
          const isValidSetTariff = setTariff.count > 0;
          const tariffIndex = findTariffIndex(setTariff, foundBookedTariffType);

          addOrRemoveTariff(tariffIndex, isValidSetTariff, setTariff, foundBookedTariffs);
        });

        removeEmptyProductType(foundBookedTariffs, cloneStateProductsList, productTypeBookingIndex);
      } else if (isBookingProductTypePackage(setProductType)) {
        const foundBookedPackageType = cloneStateProductsList[productTypeBookingIndex] as BookingPackageType;
        const foundBookedPackageProductTypes: BookingProductType[] = foundBookedPackageType.productTypes;

        setProductType.productTypes.forEach((setPackageProductType) => {
          const packageBookingTicketTypeIndex = findProductTypeBookingIndex(
            setPackageProductType,
            foundBookedPackageProductTypes
          );

          if (packageBookingTicketTypeIndex === AppConstants.INDEX_NOT_FOUND) {
            const validatedSetPackageProductType = validateProductTypes(setPackageProductType);

            if (isValidProductType(validatedSetPackageProductType)) {
              foundBookedPackageProductTypes.push(validatedSetPackageProductType);
            }
          } else {
            if (isBookingProductTypeTariff(setPackageProductType)) {
              const foundBookingPackageTariffType = foundBookedPackageProductTypes[
                packageBookingTicketTypeIndex
              ] as BookingTariffType;
              const { tariffs: bookedPackageTariffs } = foundBookingPackageTariffType;

              setPackageProductType.tariffs.forEach((setPackageTariff) => {
                const isValidSetPackageTariff = setPackageTariff.count > 0;
                const packageTariffIndex = findTariffIndex(setPackageTariff, foundBookingPackageTariffType);

                addOrRemoveTariff(packageTariffIndex, isValidSetPackageTariff, setPackageTariff, bookedPackageTariffs);
              });

              removeEmptyProductType(
                bookedPackageTariffs,
                foundBookedPackageProductTypes,
                packageBookingTicketTypeIndex
              );
            }
          }

          removeEmptyProductType(foundBookedPackageProductTypes, cloneStateProductsList, productTypeBookingIndex);
        });
      }
    });

    return {
      ...state,
      list: cloneStateProductsList
    };
  }

  return state;
};

/**
 * Remove product from booking list
 * @description Remove product from booking list
 * @param state
 * @param props
 * @returns State
 */
export const removeProductFromBookingList = (
  state: BookingState,
  props: { bookingProductTypes: BookingProductType[] }
) => {
  const cloneStateProductsList: BookingProductType[] = cloneDeep(state.list);
  const removedProductTypes: BookingProductType[] = cloneDeep(props.bookingProductTypes);

  if (removedProductTypes.length) {
    removedProductTypes.forEach((removedBookedProductType) => {
      const productTypeBookingIndex = findProductTypeBookingIndex(removedBookedProductType, cloneStateProductsList);

      if (productTypeBookingIndex !== AppConstants.INDEX_NOT_FOUND) {
        if (isBookingProductTypeTariff(removedBookedProductType)) {
          const foundBookedTariffType = cloneStateProductsList[productTypeBookingIndex] as BookingTariffType;
          const { tariffs: foundBookingTariffs } = foundBookedTariffType;

          removedBookedProductType.tariffs.forEach((removedTariff) => {
            const tariffIndex = findTariffIndex(removedTariff, foundBookedTariffType);

            if (tariffIndex !== AppConstants.INDEX_NOT_FOUND) {
              foundBookingTariffs.splice(tariffIndex, 1);
            }
          });

          removeEmptyProductType(foundBookingTariffs, cloneStateProductsList, productTypeBookingIndex);
        } else if (isBookingProductTypePackage(removedBookedProductType)) {
          const foundBookedPackageType = cloneStateProductsList[productTypeBookingIndex] as BookingPackageType;
          const foundBookedProductTypes: BookingProductType[] = foundBookedPackageType.productTypes;

          removedBookedProductType.productTypes.forEach((removeBookedPackageProductType) => {
            const packageBookingTariffTypeIndex = findProductTypeBookingIndex(
              removeBookedPackageProductType,
              foundBookedProductTypes
            );

            if (packageBookingTariffTypeIndex !== AppConstants.INDEX_NOT_FOUND) {
              const foundBookedPackageTicketType = foundBookedProductTypes[
                packageBookingTariffTypeIndex
              ] as BookingTariffType;
              const { tariffs: foundBookingPackageTariffs } = foundBookedPackageTicketType;

              if (isBookingProductTypeTariff(removeBookedPackageProductType)) {
                removeBookedPackageProductType.tariffs.forEach((removeBookedPackageTariff) => {
                  const packageTariffIndex = findTariffIndex(removeBookedPackageTariff, foundBookedPackageTicketType);

                  if (packageTariffIndex !== AppConstants.INDEX_NOT_FOUND) {
                    foundBookedPackageTicketType.tariffs.splice(packageTariffIndex, 1);
                  }
                });

                removeEmptyProductType(
                  foundBookingPackageTariffs,
                  foundBookedProductTypes,
                  packageBookingTariffTypeIndex
                );
              }
            }
          });

          removeEmptyProductType(foundBookedProductTypes, cloneStateProductsList, productTypeBookingIndex);
        }
      }
    });

    return {
      ...state,
      list: cloneStateProductsList
    };
  }

  return state;
};
